import React, { useMemo } from 'react';
import { Box, SxProps, Theme, TextField, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import BaseCard from '@/components/ui/BaseCard';
import { Add, AttachFile, Delete, Search } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import LayoutSubheading from './LayoutSubheading';
import { FilterableLayoutItem, FilterOption, LAYOUT_MODE } from '@/types/pages/layout';
import BaseButton from '../ui/BaseButton';
import BigIconButton from './BigIconButton';
import { useTranslation } from 'react-i18next';

type AvailableLayoutsCardProps<T> = {
    tabType: LAYOUT_MODE;
    layouts: T[];
    searchText: string;
    onSearchChange: (v: string) => void;
    children: (item: T) => React.ReactNode;
    sx?: SxProps<Theme>;
    hasUnsavedLayouts?: boolean;

    enableSize?: boolean;
    sizeOptions?: FilterOption[];
    selectedSizes?: string[];
    onSizeChange?: (v: string[]) => void;
    enableColor?: boolean;
    colorOptions?: FilterOption[];
    selectedColors?: string[];
    onColorChange?: (v: string[]) => void;
    viewMode?: boolean;
    onAttach?: () => void;
    onAddNew?: () => void;
    onDeleteAll?: () => void;
};

const OptionsAutocomplete = ({
    label,
    options,
    selectOptions,
    onChange,
}: {
    label: string;
    options: FilterOption[];
    selectOptions: string[];
    onChange: (prev: string[]) => void;
}) => {
    const sortedSizeOptions = useMemo(
        () =>
            [...options].sort((a, b) => {
                const na = parseFloat(a.value);
                const nb = parseFloat(b.value);
                return na - nb;
            }),
        [options],
    );

    return (
        <Autocomplete
            fullWidth
            multiple
            options={sortedSizeOptions}
            limitTags={2}
            getOptionLabel={(o) => o.label}
            value={sortedSizeOptions.filter((o) => selectOptions.includes(o.value))}
            onChange={(_, newVals) => onChange(newVals.map((o) => o.value))}
            renderInput={(params) => <TextField {...params} size="small" label={label} placeholder={label} />}
        />
    );
};
export default function AvailableLayoutsCard<T extends FilterableLayoutItem = FilterableLayoutItem>({
    tabType,
    viewMode,
    layouts,
    enableSize,
    sizeOptions,
    selectedSizes,
    onSizeChange,
    enableColor,
    colorOptions,
    selectedColors,
    onColorChange,
    searchText,
    onSearchChange,
    onDeleteAll,
    children,
    onAttach,
    onAddNew,
}: AvailableLayoutsCardProps<T>) {
    // let filtered = layouts;

    // if (enableSize) {
    //     filtered = layouts.filter((l) => selectedSizes.length === 0 || selectedSizes.includes((l as any).size));
    // }

    // if (enableColor) {
    //     filtered = filtered.filter(
    //         (l) => selectedColors.length === 0 || (l.colors?.some((c) => selectedColors.includes(c)) ?? false),
    //     );
    // }

    // if (searchText) {
    //     const lower = searchText.toLowerCase();
    //     filtered = filtered.filter(
    //         (l) => l.name.toLowerCase().includes(lower) || (l.description?.toLowerCase().includes(lower) ?? false),
    //     );
    // }
    // const [infoOpen, setInfoOpen] = useState(true);
    const { t } = useTranslation('common');
    const heading = tabType === 'MULTI' ? t('Available Sub Layouts') : t('Available Layouts');

    return (
        <BaseCard
            sx={{
                height: '99.3%',
                padding: '0.5rem 1.5rem',
                mb: 0.5,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignContent: 'center' }}>
                <LayoutSubheading>{heading}</LayoutSubheading>
                {!viewMode && (
                    <BigIconButton sx={{ mb: 1.5 }} tooltipTitle={t('Add a new layout')} onClick={onAddNew}>
                        <Add />
                    </BigIconButton>
                )}
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {enableSize && (
                    <OptionsAutocomplete
                        label={t('Size')}
                        options={sizeOptions as FilterOption[]}
                        selectOptions={selectedSizes as string[]}
                        onChange={onSizeChange as (prev: string[]) => void}
                    />
                )}
                {enableColor && (
                    <OptionsAutocomplete
                        label={t('Colors')}
                        options={colorOptions as FilterOption[]}
                        selectOptions={selectedColors as string[]}
                        onChange={onColorChange as (prev: string[]) => void}
                    />
                )}

                <OutlinedInput
                    fullWidth
                    size="small"
                    placeholder={t('Search')}
                    value={searchText}
                    sx={{ alignSelf: 'flex-start' }}
                    onChange={(e) => onSearchChange(e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton edge="end" size="small" aria-label={t('Search')}>
                                <Search />
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </Box>

            {/* Layout cards grid */}

            <BaseCard
                sx={{
                    display: 'grid',
                    alignItems: 'start',
                    gap: 2,
                    gridAutoRows: 'max-content',
                    height: 453,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                    },
                    padding: '0.5rem',
                    overflowY: 'auto',
                }}
            >
                {layouts.map((layout) => (
                    <React.Fragment key={layout.id}> {children(layout)}</React.Fragment>
                ))}
            </BaseCard>

            {!viewMode && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 2,
                        mb: 1,
                    }}
                >
                    <BaseButton startIcon={<AttachFile />} variant="contained" color="primary" onClick={onAttach}>
                        {t('Attach')}
                    </BaseButton>
                    <BaseButton startIcon={<Delete />} color="error" onClick={onDeleteAll}>
                        {t('Delete All')}
                    </BaseButton>
                </Box>
            )}
        </BaseCard>
    );
}
